import React, { useState, useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { cn } from '@/utils/utils';
import { MAX_LENGTH_NUMBER, NUMBER_FULL_VALIDATE_REGEX, NUMBER_VALIDATE_REGEX } from '@/configs/constants';

interface FormatNumberProps {
    value: string | number | null | undefined,
    setvalueOfNumericFormat?: (value: string) => void,
    className?: string,
    statusDisplay?: 'text' | 'input',
    type?: 'text' | 'tel' | 'password',
    thousandSeparator?: boolean | string,
    allowNegative?: boolean,
    decimalScale?: number,
    prefix?: string,
    suffix?: string,
    getInputRef?: (el: HTMLInputElement) => void,
    decimalSeparator?: string,
    fixedDecimalScale?: boolean,
    placeholder?: string,
    disabled?: boolean,
    maxLength?: number,
    allowFullWidthNumber?: boolean
}

const FormatNumberVer2: React.FC<FormatNumberProps> = ({
    value,
    setvalueOfNumericFormat,
    className,
    statusDisplay = 'text',
    type = 'text',
    thousandSeparator = true,
    allowNegative = false,
    decimalScale,
    prefix = '',
    suffix = '',
    getInputRef,
    decimalSeparator = '.',
    fixedDecimalScale = false,
    placeholder,
    disabled = false,
    maxLength = MAX_LENGTH_NUMBER,
    allowFullWidthNumber = true
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState<string>('');
    const rawInputRef = useRef<HTMLInputElement>(null);
    // const isComposing = useRef(false);

    useEffect(() => {
        if (!isEditing) {
            setTempValue(value == null ? '' : String(value));
        }
    }, [value, isEditing]);

    const normalizeFullWidthNumber = (str: string) =>
        str.replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFF10 + 0x30))
            .replace(/、/g, '')
            .replace(/。/g, '.')
            .replace(/－/g, '-');

    const handleFormattedFocus = () => {
        if (disabled || statusDisplay !== 'input') return;
        setTempValue(value == null ? '' : String(value));
        setIsEditing(true);
        setTimeout(() => rawInputRef.current?.focus(), 0);
    };

    const handleRawChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let txt = e.target.value;
        if (txt.length > maxLength) txt = txt.slice(0, maxLength);
        setTempValue(txt);
        // console.log('txt', txt);
    };

    const handleRawPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (!allowFullWidthNumber) return;
        const pasted = e.clipboardData.getData('text');
        if (/[０-９，．－]/.test(pasted)) {
            e.preventDefault();
            const normalized = normalizeFullWidthNumber(pasted);
            const input = e.target as HTMLInputElement;
            const start = input.selectionStart ?? 0;
            const end = input.selectionEnd ?? 0;
            let newVal = input.value.slice(0, start) + normalized + input.value.slice(end);
            newVal = newVal.slice(0, maxLength);
            input.value = newVal;
            const event = new Event('input', { bubbles: true });
            input.dispatchEvent(event);
        }
    };

    const handleRawBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        let raw = e.target.value || '';
        if (allowFullWidthNumber) raw = normalizeFullWidthNumber(raw);
        // callback raw (empty or with letters or digits)
        setvalueOfNumericFormat?.(raw);
        setIsEditing(false);
        // setTempValue(raw);
        // console.log('raw', raw);
    };

    const handleRawKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') rawInputRef.current?.blur();
    };

    // Display-only mode
    if (statusDisplay === 'text') {
        const rawVal = value == null ? '' : String(value);
        const onlyDigits = allowNegative ? NUMBER_FULL_VALIDATE_REGEX.test(rawVal) : NUMBER_VALIDATE_REGEX.test(rawVal);
        const isNum = typeof value === 'number';

        if (rawVal && (onlyDigits || isNum)) {
            return (
                <NumericFormat
                    className={cn(
                        'text-sm text-text flex h-12 w-full rounded-base border border-border-input bg-background-input px-3 py-2 transition-colors duration-300 placeholder:text-placeholder',
                        'focus-visible:outline-0 focus-visible:border-primaryWeb',
                        // 'disabled:cursor-not-allowed disabled:!bg-disable disabled:hover:border-disable',
                        disabled && '!cursor-not-allowed !bg-disable hover:!border-disable',
                        'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                        'hover:border-primaryWeb',
                        className
                    )}
                    displayType='text'
                    thousandSeparator={thousandSeparator}
                    decimalScale={decimalScale}
                    allowNegative={allowNegative}
                    value={rawVal}
                    prefix={prefix}
                    suffix={suffix}
                    decimalSeparator={decimalSeparator}
                    fixedDecimalScale={fixedDecimalScale}
                    placeholder={placeholder}
                    disabled={disabled}
                    getInputRef={getInputRef}
                    renderText={formattedValue =>
                        !formattedValue.trim() ?
                            (
                                <span className='text-placeholder'>{placeholder}</span>
                            ) :
                            (
                                <>{formattedValue}</>
                            )}
                />
            );
        }
        return (
            <div
                className={cn(
                    'text-sm text-text flex h-12 w-full items-center rounded-base border border-border-input bg-background-input px-3 transition-colors duration-300 placeholder:text-placeholder',
                    'focus-visible:outline-0 focus-visible:border-primaryWeb',
                    disabled && '!cursor-not-allowed !bg-disable hover:!border-disable',
                    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                    'hover:border-primaryWeb',
                    className
                )}
            >
                {rawVal || <span className='text-placeholder'>{placeholder}</span>}
            </div>
        );
    }

    // Input mode
    if (isEditing) {
        return (
            <input
                ref={rawInputRef}
                key='raw'
                className={cn(
                    'text-sm text-text flex h-12 w-full rounded-base border border-border-input bg-background-input px-3 py-2 transition-colors duration-300 placeholder:text-placeholder',
                    'focus-visible:outline-0 focus-visible:border-primaryWeb',
                    'disabled:cursor-not-allowed disabled:!bg-disable disabled:hover:border-disable',
                    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                    'hover:border-primaryWeb',
                    className
                )}
                type={type}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                value={tempValue}
                onChange={handleRawChange}
                onCompositionStart={(e) => {
                    // isComposing.current = true;
                    // console.log(e);
                }}
                onCompositionEnd={(e) => {
                    handleRawBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
                }}
                onBlur={handleRawBlur}
                onPaste={handleRawPaste}
                onKeyDown={handleRawKeyDown}
            />
        );
    }

    // Click-to-edit display
    const rawVal2 = value == null ? '' : String(value);
    const onlyDigits2 = allowNegative ? NUMBER_FULL_VALIDATE_REGEX.test(rawVal2) : NUMBER_VALIDATE_REGEX.test(rawVal2);
    const isNum2 = typeof value === 'number';

    if (rawVal2 && (onlyDigits2 || isNum2)) {
        return (
            <NumericFormat
                className={cn(
                    'text-sm flex h-12 w-full items-center rounded-base border border-border-input bg-background-input px-3 transition-colors duration-300',
                    'focus-visible:outline-0 focus-visible:border-primaryWeb',
                    // 'disabled:cursor-not-allowed disabled:!bg-disable disabled:hover:border-disable',
                    disabled && '!cursor-not-allowed !bg-disable hover:!border-disable',
                    'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                    'hover:border-primaryWeb',
                    className
                )}
                key='display'
                displayType='text'
                thousandSeparator={thousandSeparator}
                decimalScale={decimalScale}
                allowNegative={allowNegative}
                value={rawVal2}
                prefix={prefix}
                suffix={suffix}
                decimalSeparator={decimalSeparator}
                fixedDecimalScale={fixedDecimalScale}
                placeholder={placeholder}
                disabled={disabled}
                getInputRef={getInputRef}
                onClick={handleFormattedFocus}
            />
        );
    }

    return (
        <div
            className={cn(
                'text-sm flex h-12 w-full items-center rounded-base border border-border-input bg-background-input px-3 transition-colors duration-300 placeholder:text-placeholder',
                'focus-visible:outline-0 focus-visible:border-primaryWeb',
                disabled && '!cursor-not-allowed !bg-disable hover:!border-disable',
                'file:border-0 file:bg-transparent file:text-sm file:font-medium',
                'hover:border-primaryWeb',
                className
            )}
            onClick={handleFormattedFocus}
            tabIndex={disabled ? undefined : 0}
            role={disabled ? undefined : 'button'}
            style={{ cursor: disabled ? 'not-allowed' : 'text', userSelect: 'none' }}
        >
            {rawVal2 || <span className='text-placeholder'>{placeholder}</span>}
        </div>
    );
};

export default FormatNumberVer2;
